import React, { useMemo } from 'react';
import {
    Box,
    Button,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { DialogV2 } from 'c-components';
import { useCommonTranslation } from 'c-translation';
import { useDateUtils } from 'c-hooks';
import { format } from 'date-fns';
import type { StatusedPostingLine } from './lib';
import { diffDays, PostingStatus } from './lib';

const p = 'Modules.Main.Campaigns.ClassicPostings';

type Props = {
    open: boolean;
    onClose: () => void;
    /** Closes the dialog and filters the posting lines to awaiting. */
    onShowInLines: () => void;
    lines: StatusedPostingLine[];
    dataThrough: string | null;
};

const AwaitingScreensDialog: React.FC<Props> = ({
    open,
    onClose,
    onShowInLines,
    lines,
    dataThrough,
}) => {
    const t = useCommonTranslation();
    const { formatDateString } = useDateUtils();
    // Waiting is measured to the latest data; before any has arrived, to today.
    const asOf = dataThrough ?? format(new Date(), 'yyyy-MM-dd');

    const rows = useMemo(
        () =>
            lines
                .filter(l => l.status === PostingStatus.Awaiting)
                .map(l => ({
                    ...l,
                    waiting:
                        l.bookedStart && asOf ? Math.max(0, diffDays(l.bookedStart, asOf)) : null,
                }))
                .sort(
                    (a, b) =>
                        (a.bookedStart ?? '').localeCompare(b.bookedStart ?? '') ||
                        a.owner.localeCompare(b.owner),
                ),
        [lines, asOf],
    );
    const owners = useMemo(() => new Set(rows.map(r => r.owner)).size, [rows]);
    const longest = useMemo(() => {
        const waits = rows.map(r => r.waiting).filter(w => w != null);
        return waits.length ? Math.max(...waits) : null;
    }, [rows]);

    const stats = [
        { label: t(`${p}.Awaiting.screens`), value: rows.length },
        { label: t(`${p}.Awaiting.owners`), value: owners },
        {
            label: t(`${p}.Awaiting.longestWait`),
            value: longest == null ? '—' : t(`${p}.Awaiting.days`, { days: longest }),
        },
    ];

    return (
        <DialogV2
            open={open}
            onClose={onClose}
            title={t(`${p}.Awaiting.title`)}
            maxWidth="md"
            actions={
                <Stack direction="row" gap={1}>
                    <Button variant="outlined" onClick={onClose}>
                        {t(`${p}.Awaiting.close`)}
                    </Button>
                    <Button variant="contained" onClick={onShowInLines}>
                        {t(`${p}.Awaiting.showInLines`)}
                    </Button>
                </Stack>
            }
        >
            <Stack direction="row" gap={4} mb={2} flexWrap="wrap">
                {stats.map(s => (
                    <Box key={s.label}>
                        <Typography variant="caption" color="text.secondary">
                            {s.label}
                        </Typography>
                        <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {s.value}
                        </Typography>
                    </Box>
                ))}
            </Stack>
            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 600 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t(`${p}.Table.unit`)}</TableCell>
                            <TableCell>{t(`${p}.Table.owner`)}</TableCell>
                            <TableCell>{t(`${p}.Table.market`)}</TableCell>
                            <TableCell align="right">{t(`${p}.Table.bookedStart`)}</TableCell>
                            <TableCell align="right">{t(`${p}.Awaiting.waiting`)}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(r => (
                            <TableRow key={r.displayId}>
                                <TableCell>
                                    <Typography variant="body2" fontWeight={600}>
                                        {r.name || r.frameId}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {[
                                            t(`${p}.Table.frame`, { frame: r.frameId }),
                                            r.postcode,
                                            r.state,
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </Typography>
                                </TableCell>
                                <TableCell>{r.owner}</TableCell>
                                <TableCell>{r.market}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    {r.bookedStart
                                        ? formatDateString({ date: r.bookedStart })
                                        : '—'}
                                </TableCell>
                                <TableCell align="right">
                                    {r.waiting == null
                                        ? '—'
                                        : t(`${p}.Awaiting.days`, { days: r.waiting })}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </DialogV2>
    );
};

export default AwaitingScreensDialog;
